import { FormEvent, useState } from 'react';
import { format } from 'date-fns';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import type { ActionMeta, SingleValue, StylesConfig } from 'react-select';

import { DefaultSelectStyle } from 'components/Shared/Forms/CompactSelect/styles';
import type {
  YardGateCatalogs as YardGateCatalogsContract,
  YardGateOption,
} from 'contracts/YardGate';
import { ColorScheme, Colors, Fonts } from 'styles/constants';
import * as S from '../../styles';

export interface CreateYardGateEntryInput {
  date: string;
  time: string;
  driverCpf: string;
  driverName: string;
  phone: string;
  observation: string;
  orderTypeId: number | null;
  carrierId: number | null;
  vehicleTypeId: number | null;
  plate: string;
  areaId: number;
  authorizerId: number;
  attachments: File[];
}

interface EntryFormProps {
  catalogs: YardGateCatalogsContract;
  authorizers: YardGateOption[];
  isSaving: boolean;
  isCatalogLoading: boolean;
  onSubmit: (input: CreateYardGateEntryInput) => void;
  onCreateAuthorizer: (name: string) => Promise<YardGateOption | null>;
  onCancel: () => void;
}

type AuthorizerOption = { value: number | string; label: string };

const baseFormSelectStyle = DefaultSelectStyle as StylesConfig<
  AuthorizerOption,
  false
>;

const formSelectStyle: StylesConfig<AuthorizerOption, false> = {
  ...baseFormSelectStyle,
  container: (base, state) => ({
    ...(baseFormSelectStyle.container?.(base, state) ?? base),
    width: '100%',
  }),
  control: (base, state) => ({
    ...(baseFormSelectStyle.control?.(base, state) ?? base),
    'minHeight': '38px',
    'height': '38px',
    'borderColor': Colors.Gray40,
    'borderRadius': '6px',
    'boxShadow': 'none',
    'fontFamily': Fonts.GilroyRegular,
    'fontSize': '14px',
    'backgroundColor': state.isDisabled
      ? String(ColorScheme.DisabledField)
      : Colors.White,
    ':hover': { borderColor: Colors.Gray40 },
  }),
  valueContainer: (base, state) => ({
    ...(baseFormSelectStyle.valueContainer?.(base, state) ?? base),
    padding: '0 12px',
  }),
  indicatorsContainer: (base) => ({ ...base, height: '36px' }),
  menuPortal: (base, state) => ({
    ...(baseFormSelectStyle.menuPortal?.(base, state) ?? base),
    zIndex: 100,
  }),
  placeholder: (base) => ({
    ...base,
    color: String(ColorScheme.LightText),
    fontFamily: Fonts.GilroyRegular,
  }),
  option: (base, state) => ({
    ...(baseFormSelectStyle.option?.(base, state) ?? base),
    fontFamily: Fonts.GilroyRegular,
    fontSize: '14px',
  }),
};

const catalogOption = (
  options: YardGateOption[],
  value: number | ''
): AuthorizerOption | null =>
  value === '' ? null : options.find((option) => option.value === value) ?? null;

const catalogValue = (option: SingleValue<AuthorizerOption>): number | '' =>
  option ? Number(option.value) : '';

type EntryDraft = Omit<
  CreateYardGateEntryInput,
  | 'orderTypeId'
  | 'carrierId'
  | 'vehicleTypeId'
  | 'areaId'
  | 'authorizerId'
  | 'attachments'
> & {
  orderTypeId: number | '';
  carrierId: number | '';
  vehicleTypeId: number | '';
  areaId: number | '';
  authorizerId: number | '';
};
type EntryErrors = Partial<Record<keyof EntryDraft, string>>;

const ACCEPTED_ATTACHMENT_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'];
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 3;

const attachmentExtension = (file: File): string =>
  file.name.split('.').pop()?.toLowerCase() ?? '';

const validateAttachments = (files: File[]): string => {
  if (files.length > MAX_ATTACHMENTS) {
    return 'É permitido manter no máximo 3 anexos.';
  }
  if (
    files.some(
      (file) =>
        !ACCEPTED_ATTACHMENT_EXTENSIONS.includes(attachmentExtension(file))
    )
  ) {
    return 'Envie apenas arquivos PDF ou imagens (png/jpg).';
  }
  if (files.some((file) => file.size > MAX_ATTACHMENT_SIZE)) {
    return 'Cada arquivo deve ter no máximo 10MB.';
  }
  return '';
};

const emptyDraft = (): EntryDraft => ({
  date: format(new Date(), 'yyyy-MM-dd'),
  time: '',
  driverCpf: '',
  driverName: '',
  phone: '',
  observation: '',
  orderTypeId: '',
  carrierId: '',
  vehicleTypeId: '',
  plate: '',
  areaId: '',
  authorizerId: '',
});

const validate = (draft: EntryDraft): EntryErrors => {
  const errors: EntryErrors = {};

  if (!draft.date) errors.date = 'Informe a data.';
  if (!draft.time) errors.time = 'Informe a hora.';
  if (draft.driverCpf.replace(/\D/g, '').length !== 11) {
    errors.driverCpf = 'Informe um CPF com 11 dígitos.';
  }
  if (!draft.driverName.trim())
    errors.driverName = 'Informe o nome do motorista.';
  const phoneDigits = draft.phone.replace(/\D/g, '');
  if (phoneDigits && ![10, 11].includes(phoneDigits.length)) {
    errors.phone = 'Informe um telefone com 10 ou 11 dígitos.';
  }
  if (!draft.plate.trim()) errors.plate = 'Informe a placa.';
  if (draft.areaId === '') errors.areaId = 'Informe a área.';
  if (!draft.authorizerId) {
    errors.authorizerId = 'Informe quem autorizou a entrada.';
  }
  if (draft.observation.length > 500) {
    errors.observation = 'A anotação deve ter no máximo 500 caracteres.';
  }

  return errors;
};

export const EntryForm = ({
  catalogs,
  authorizers,
  isSaving,
  isCatalogLoading,
  onSubmit,
  onCreateAuthorizer,
  onCancel,
}: EntryFormProps) => {
  const [draft, setDraft] = useState<EntryDraft>(emptyDraft);
  const [errors, setErrors] = useState<EntryErrors>({});
  const [authorizer, setAuthorizer] = useState<AuthorizerOption | null>(null);
  const [isCreatingAuthorizer, setIsCreatingAuthorizer] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState('');

  const updateDraft = <Key extends keyof EntryDraft>(
    field: Key,
    value: EntryDraft[Key]
  ) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleAuthorizerChange = async (
    option: SingleValue<AuthorizerOption>,
    actionMeta: ActionMeta<AuthorizerOption>
  ) => {
    if (actionMeta.action === 'clear' || !option) {
      setAuthorizer(null);
      updateDraft('authorizerId', '');
      return;
    }

    if (typeof option.value === 'number') {
      setAuthorizer(option);
      updateDraft('authorizerId', option.value);
      return;
    }

    const name = option.label.trim();
    if (!name) {
      setAuthorizer(null);
      updateDraft('authorizerId', '');
      return;
    }

    setAuthorizer({ value: option.value, label: name });
    setIsCreatingAuthorizer(true);
    const created = await onCreateAuthorizer(name);
    setIsCreatingAuthorizer(false);

    if (created) {
      setAuthorizer(created);
      updateDraft('authorizerId', created.value);
      return;
    }

    setAuthorizer(null);
    updateDraft('authorizerId', '');
    setErrors((current) => ({
      ...current,
      authorizerId: 'Não foi possível cadastrar o autorizador.',
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate(draft);
    const nextAttachmentError = validateAttachments(attachments);
    setErrors(nextErrors);
    setAttachmentError(nextAttachmentError);
    if (Object.keys(nextErrors).length || nextAttachmentError) return;
    onSubmit({
      date: draft.date,
      time: draft.time,
      driverCpf: draft.driverCpf.replace(/\D/g, ''),
      driverName: draft.driverName.trim(),
      phone: draft.phone.replace(/\D/g, ''),
      observation: draft.observation,
      orderTypeId: draft.orderTypeId === '' ? null : Number(draft.orderTypeId),
      carrierId: draft.carrierId === '' ? null : Number(draft.carrierId),
      vehicleTypeId:
        draft.vehicleTypeId === '' ? null : Number(draft.vehicleTypeId),
      plate: draft.plate.replace(/\s/g, '').toUpperCase(),
      areaId: Number(draft.areaId),
      authorizerId: Number(draft.authorizerId),
      attachments,
    });
  };

  const handleCancel = () => {
    setDraft(emptyDraft());
    setErrors({});
    setAuthorizer(null);
    setAttachments([]);
    setAttachmentError('');
    onCancel();
  };

  const errorProps = (field: keyof EntryDraft) =>
    errors[field] ? { 'aria-describedby': `yard-gate-${field}-error` } : {};

  const renderError = (field: keyof EntryDraft) =>
    errors[field] && (
      <S.FieldError id={`yard-gate-${field}-error`}>
        {errors[field]}
      </S.FieldError>
    );

  return (
    <S.EntryForm onSubmit={handleSubmit} noValidate>
      <S.FormGrid>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-date">Data</S.Label>
          <S.Input
            id="yard-gate-entry-date"
            type="date"
            value={draft.date}
            onChange={(event) => updateDraft('date', event.target.value)}
            {...errorProps('date')}
          />
          {renderError('date')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-time">Hora</S.Label>
          <S.Input
            id="yard-gate-entry-time"
            type="time"
            value={draft.time}
            onChange={(event) => updateDraft('time', event.target.value)}
            {...errorProps('time')}
          />
          {renderError('time')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-driver-cpf">CPF Motorista</S.Label>
          <S.Input
            id="yard-gate-entry-driver-cpf"
            value={draft.driverCpf}
            onChange={(event) => updateDraft('driverCpf', event.target.value)}
            {...errorProps('driverCpf')}
          />
          {renderError('driverCpf')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-driver-name">
            Nome Motorista
          </S.Label>
          <S.Input
            id="yard-gate-entry-driver-name"
            value={draft.driverName}
            onChange={(event) => updateDraft('driverName', event.target.value)}
            {...errorProps('driverName')}
          />
          {renderError('driverName')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-phone">Telefone</S.Label>
          <S.Input
            id="yard-gate-entry-phone"
            inputMode="tel"
            value={draft.phone}
            onChange={(event) => updateDraft('phone', event.target.value)}
            {...errorProps('phone')}
          />
          {renderError('phone')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-order-type">Tipo ordem</S.Label>
          <ReactSelect<AuthorizerOption, false>
            inputId="yard-gate-entry-order-type"
            aria-label="Tipo ordem"
            isClearable
            menuPortalTarget={document.body}
            menuPlacement="top"
            styles={formSelectStyle}
            placeholder="N/A"
            options={catalogs.orderTypes}
            value={catalogOption(catalogs.orderTypes, draft.orderTypeId)}
            isDisabled={isSaving || isCatalogLoading}
            onChange={(option) =>
              updateDraft('orderTypeId', catalogValue(option))
            }
            {...errorProps('orderTypeId')}
          />
          {renderError('orderTypeId')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-carrier">Transportadora</S.Label>
          <ReactSelect<AuthorizerOption, false>
            inputId="yard-gate-entry-carrier"
            aria-label="Transportadora"
            isClearable
            menuPortalTarget={document.body}
            menuPlacement="top"
            styles={formSelectStyle}
            placeholder="N/A"
            options={catalogs.carriers}
            value={catalogOption(catalogs.carriers, draft.carrierId)}
            isDisabled={isSaving || isCatalogLoading}
            onChange={(option) =>
              updateDraft('carrierId', catalogValue(option))
            }
            {...errorProps('carrierId')}
          />
          {renderError('carrierId')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-vehicle-type">
            Tipo de Veículo
          </S.Label>
          <ReactSelect<AuthorizerOption, false>
            inputId="yard-gate-entry-vehicle-type"
            aria-label="Tipo de Veículo"
            isClearable
            menuPortalTarget={document.body}
            menuPlacement="top"
            styles={formSelectStyle}
            placeholder="N/A"
            options={catalogs.vehicleTypes}
            value={catalogOption(catalogs.vehicleTypes, draft.vehicleTypeId)}
            isDisabled={isSaving || isCatalogLoading}
            onChange={(option) =>
              updateDraft('vehicleTypeId', catalogValue(option))
            }
            {...errorProps('vehicleTypeId')}
          />
          {renderError('vehicleTypeId')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-plate">Placa</S.Label>
          <S.Input
            id="yard-gate-entry-plate"
            value={draft.plate}
            onChange={(event) => updateDraft('plate', event.target.value)}
            {...errorProps('plate')}
          />
          {renderError('plate')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-area">Área</S.Label>
          <ReactSelect<AuthorizerOption, false>
            inputId="yard-gate-entry-area"
            aria-label="Área"
            menuPortalTarget={document.body}
            menuPlacement="top"
            styles={formSelectStyle}
            placeholder="Selecione"
            options={catalogs.areas}
            value={catalogOption(catalogs.areas, draft.areaId)}
            isDisabled={isSaving || isCatalogLoading}
            onChange={(option) => updateDraft('areaId', catalogValue(option))}
            {...errorProps('areaId')}
          />
          {renderError('areaId')}
        </S.Field>
        <S.Field>
          <S.Label htmlFor="yard-gate-entry-authorizer">
            Autorizado entrada por
          </S.Label>
          <CreatableSelect<AuthorizerOption, false>
            inputId="yard-gate-entry-authorizer"
            options={authorizers}
            value={authorizer}
            onChange={handleAuthorizerChange}
            isClearable
            isDisabled={isSaving || isCreatingAuthorizer}
            isLoading={isCatalogLoading}
            placeholder="Nome do gestor"
            formatCreateLabel={(input) => `Cadastrar "${input.trim()}"`}
            isValidNewOption={(inputValue, _value, options) => {
              const normalized = inputValue.trim().toLowerCase();
              return (
                normalized.length > 0 &&
                !options.some(
                  (option) =>
                    'label' in option &&
                    typeof option.label === 'string' &&
                    option.label.trim().toLowerCase() === normalized
                )
              );
            }}
            noOptionsMessage={() => 'Nenhum autorizador encontrado'}
            menuPortalTarget={document.body}
            menuPlacement="top"
            styles={formSelectStyle}
            {...errorProps('authorizerId')}
          />
          {renderError('authorizerId')}
        </S.Field>
      </S.FormGrid>
      <S.Field>
        <S.Label htmlFor="yard-gate-entry-observation">Anotações</S.Label>
        <S.Textarea
          id="yard-gate-entry-observation"
          maxLength={500}
          value={draft.observation}
          onChange={(event) => updateDraft('observation', event.target.value)}
          {...errorProps('observation')}
        />
        {renderError('observation')}
      </S.Field>
      <S.Field>
        <S.Label htmlFor="yard-gate-entry-attachments">
          Anexos (opcional, até 3 arquivos)
        </S.Label>
        <S.FileInput
          id="yard-gate-entry-attachments"
          aria-label="Anexos"
          aria-describedby={
            attachmentError ? 'yard-gate-entry-attachments-error' : undefined
          }
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          multiple
          disabled={isSaving}
          onChange={(event) => {
            const selected = Array.from(event.target.files ?? []);
            setAttachments(selected);
            setAttachmentError(validateAttachments(selected));
          }}
        />
        {attachments.length ? (
          <span>{attachments.map((file) => file.name).join(', ')}</span>
        ) : null}
        {attachmentError ? (
          <S.FieldError id="yard-gate-entry-attachments-error">
            {attachmentError}
          </S.FieldError>
        ) : null}
      </S.Field>
      <S.FormActions>
        <S.Button
          mood="light"
          type="button"
          disabled={isSaving}
          onClick={handleCancel}
        >
          Cancelar
        </S.Button>
        <S.Button type="submit" disabled={isSaving || isCreatingAuthorizer}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </S.Button>
      </S.FormActions>
    </S.EntryForm>
  );
};
